import { getEchoClient } from './echoClient';

const normalizeChannelKey = (channel, type) => `${type}:${channel}`;

const normalizeEventName = (eventName) => {
  if (!eventName) {
    return '';
  }

  if (typeof eventName !== 'string') {
    return String(eventName);
  }

  return eventName.startsWith('.') ? eventName.slice(1) : eventName;
};

class NotificationGateway {
  constructor() {
    this.channels = new Map();
    this.globalHandlers = new Set();
  }

  subscribe(subscription) {
    if (!subscription || !subscription.channel) {
      return () => {};
    }

    const type = subscription.type || 'private';
    const entry = this.ensureChannel(subscription.channel, type, subscription.options);

    const normalized = {
      handler: subscription.onMessage || subscription.handler,
      events: this.normalizeEvents(subscription.events),
      metadata: subscription.metadata || {},
    };

    entry.subscribers.add(normalized);

    return () => {
      entry.subscribers.delete(normalized);
      if (entry.subscribers.size === 0) {
        this.teardownChannel(subscription.channel, type);
      }
    };
  }

  onAny(handler) {
    if (typeof handler !== 'function') {
      return () => {};
    }

    this.globalHandlers.add(handler);

    return () => {
      this.globalHandlers.delete(handler);
    };
  }

  ensureChannel(channelName, type, options = {}) {
    const key = normalizeChannelKey(channelName, type);

    if (this.channels.has(key)) {
      return this.channels.get(key);
    }

    const echo = getEchoClient(options.echoOverrides);
    if (!echo) {
      return {
        channelRef: null,
        subscribers: new Set(),
        teardown: () => {},
        channelName,
        type,
      };
    }

    let channelRef;

    if (type === 'presence') {
      channelRef = echo.join(channelName);
    } else if (type === 'private') {
      channelRef = echo.private(channelName);
    } else {
      channelRef = echo.channel(channelName);
    }

    const subscribers = new Set();

    const listener = (eventName, payload) => {
      const eventNameStr = typeof eventName === 'string' ? eventName : String(eventName);

      const normalizedEvent = normalizeEventName(eventNameStr);
      this.dispatchEvent({
        channel: channelName,
        type,
        event: normalizedEvent,
        payload,
      });
    };

    if (typeof channelRef.listenToAll === 'function') {
      channelRef.listenToAll(listener);
    }

    if (typeof channelRef.notification === 'function') {
      channelRef.notification((notification) => {
        this.dispatchEvent({
          channel: channelName,
          type,
          event: 'notification',
          payload: notification,
        });
      });
    }

    const teardown = (channelNameToLeave, typeToLeave) => {
      const name = channelNameToLeave || channelName;
      const channelType = typeToLeave || type;

      if (!name) {
        return;
      }

      try {
        if (channelRef) {
          if (typeof channelRef.stopListening === 'function') {
            channelRef.stopListening();
          }

          if (typeof channelRef.unsubscribe === 'function') {
            channelRef.unsubscribe();
          }
        }

        const echoClient = getEchoClient();
        if (echoClient) {
          if (channelType === 'presence' && typeof echoClient.leaveChannel === 'function') {
            echoClient.leaveChannel(name);
          } else if (typeof echoClient.leave === 'function') {
            echoClient.leave(name);
          }
        }
      } catch (error) {
        console.log('teardown error', error);
      }
    };

    const entry = {
      channelRef,
      subscribers,
      teardown,
      channelName,
      type,
    };

    this.channels.set(key, entry);
    return entry;
  }

  teardownChannel(channelName, type) {
    if (!channelName || !type) {
      return;
    }

    const key = normalizeChannelKey(channelName, type);
    const entry = this.channels.get(key);

    if (!entry) {
      return;
    }

    entry.teardown(channelName, type);
    this.channels.delete(key);
  }

  dispatchEvent(event) {
    if (!event.event) {
      return;
    }

    const normalizedEvent =
      typeof event.event === 'string' ? event.event.toLowerCase() : null;

    if (
      normalizedEvent &&
      (normalizedEvent.startsWith('pusher:') || normalizedEvent.startsWith('pusher_internal'))
    ) {
      return;
    }

    this.globalHandlers.forEach((handler) => {
      handler(event);
    });

    const key = normalizeChannelKey(event.channel, event.type);
    const entry = this.channels.get(key);

    if (!entry) {
      return;
    }

    entry.subscribers.forEach((subscriber) => {
      if (!subscriber.handler) {
        return;
      }

      if (subscriber.events && !subscriber.events.has(event.event)) {
        return;
      }

      subscriber.handler(event);
    });
  }

  normalizeEvents(events) {
    if (!events || events.length === 0) {
      return null;
    }

    const array = Array.isArray(events) ? events : [events];
    const normalized = array
      .map((eventName) => normalizeEventName(eventName))
      .filter((value) => value);

    if (normalized.length === 0) {
      return null;
    }

    return new Set(normalized);
  }
}

let gatewayInstance = null;

export const getNotificationGateway = () => {
  if (!gatewayInstance) {
    gatewayInstance = new NotificationGateway();
  }

  return gatewayInstance;
};

export const resetNotificationGateway = () => {
  if (gatewayInstance) {
    const channels = Array.from(gatewayInstance.channels.values());
    channels.forEach((entry) => {
      if (entry.channelName && entry.type && entry.teardown) {
        try {
          entry.teardown(entry.channelName, entry.type);
        } catch (error) {
          console.log('resetNotificationGateway teardown error', error);
        }
      }
    });
    gatewayInstance.channels.clear();
    gatewayInstance.globalHandlers.clear();
  }
  gatewayInstance = null;
};
